(() => {
  const SELECTORS = {
    drawerDetails: '#Details-menu-drawer-container',
    drawer: '#menu-drawer',
    drawerOverlay: '.menu-drawer__overlay, [data-close-drawer]',
    desktopMenu: '.header__inline-menu details',
    desktopMegaLink: '.header__inline-menu details[open] .mega-menu__content a',
    searchDetails: '[data-rp-header-search] details[open]',
  };

  const STICKY_TYPES = new Set(['none', 'always', 'on-scroll-up', 'reduce-logo-size']);

  class RPStickyHeader extends HTMLElement {
    static get observedAttributes() {
      return ['data-sticky-type'];
    }

    connectedCallback() {
      this.abortController?.abort();
      this.section = this.closest('.section-header');
      this.stickyType = this.normalizeStickyType(this.dataset.stickyType);
      this.lastScrollTop = this.getScrollTop();
      this.preventReveal = false;
      this.desktopMenus = Array.from(this.querySelectorAll(SELECTORS.desktopMenu));
      this.currentMenu = null;
      this.abortController = new AbortController();
      this.closeMenuTimer = null;
      this.preventRevealTimer = null;
      this.scrollFrame = null;
      this.metricsFrame = null;
      this.headerThreshold = 72;

      document.documentElement.classList.add('rp-header-controller');
      this.bindGlobalEvents();
      this.bindDesktopMenus();
      this.syncMetrics();
      this.applyStickyState(true);
      this.syncPanelTop();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.clearTimeout(this.closeMenuTimer);
      window.clearTimeout(this.preventRevealTimer);
      if (this.scrollFrame !== null) window.cancelAnimationFrame(this.scrollFrame);
      if (this.metricsFrame !== null) window.cancelAnimationFrame(this.metricsFrame);
      document.documentElement.classList.remove('rp-header-visible', 'rp-header-hidden');
      window.requestAnimationFrame(() => {
        if (!document.querySelector('sticky-header')) {
          document.documentElement.classList.remove('rp-header-controller');
          document.documentElement.style.removeProperty('--rp-menu-panel-top');
        }
      });
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name !== 'data-sticky-type' || oldValue === newValue || !this.isConnected) return;
      this.stickyType = this.normalizeStickyType(newValue);
      this.applyStickyState(true);
    }

    get signal() {
      return this.abortController.signal;
    }

    normalizeStickyType(value) {
      return STICKY_TYPES.has(value) ? value : 'none';
    }

    bindGlobalEvents() {
      window.addEventListener('scroll', () => this.scheduleScroll(), { passive: true, signal: this.signal });
      window.addEventListener('resize', () => this.scheduleMetricsRefresh(), { passive: true, signal: this.signal });
      window.addEventListener('orientationchange', () => this.scheduleMetricsRefresh(), { passive: true, signal: this.signal });
      document.addEventListener('click', (event) => this.handleClick(event), { signal: this.signal });
      document.addEventListener('keydown', (event) => this.handleKeydown(event), { signal: this.signal });
      document.addEventListener('visibilitychange', () => this.scheduleMetricsRefresh(), { signal: this.signal });
      this.addEventListener('preventHeaderReveal', () => this.handlePreventHeaderReveal(), { signal: this.signal });

      const drawerDetails = this.querySelector(SELECTORS.drawerDetails);
      drawerDetails?.addEventListener('toggle', () => this.syncDrawerState(), { signal: this.signal });
    }

    scheduleScroll() {
      if (this.scrollFrame !== null) return;
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.handleScroll();
      });
    }

    scheduleMetricsRefresh() {
      if (this.metricsFrame !== null) return;
      this.metricsFrame = window.requestAnimationFrame(() => {
        this.metricsFrame = null;
        this.syncMetrics();
        this.syncPanelTop();
        this.applyStickyState(false);
      });
    }
    bindDesktopMenus() {
      this.desktopMenus.forEach((details) => {
        details.addEventListener('mouseenter', () => this.openDesktopMenu(details), { signal: this.signal });
        details.addEventListener('focusin', () => this.openDesktopMenu(details), { signal: this.signal });
        details.addEventListener('mouseleave', (event) => this.scheduleCloseDesktopMenu(details, event), { signal: this.signal });
        details.addEventListener('focusout', (event) => this.handleMenuFocusOut(details, event), { signal: this.signal });
        details.addEventListener('toggle', () => {
          details.querySelector('summary')?.setAttribute('aria-expanded', String(details.open));
          if (details.open) this.openDesktopMenu(details);
        }, { signal: this.signal });
      });
    }

    handleScroll() {
      this.applyStickyState(false);
    }

    applyStickyState(forceReveal = false) {
      const currentScroll = this.getScrollTop();
      const delta = currentScroll - this.lastScrollTop;
      const pastHeader = currentScroll > this.getHeaderThreshold();
      const shouldStick = this.stickyType === 'always' || this.stickyType === 'reduce-logo-size' || (this.stickyType === 'on-scroll-up' && pastHeader);

      this.section?.classList.toggle('shopify-section-header-sticky', shouldStick);
      this.section?.classList.toggle('scrolled-past-header', this.stickyType === 'reduce-logo-size' && pastHeader);

      if (this.stickyType === 'none') {
        this.reveal({ force: true });
        this.section?.classList.remove('shopify-section-header-sticky', 'shopify-section-header-hidden', 'scrolled-past-header');
        this.lastScrollTop = currentScroll;
        return;
      }

      if (this.stickyType === 'always' || this.stickyType === 'reduce-logo-size' || this.hasOpenPanel() || forceReveal) {
        this.reveal({ force: true });
        this.lastScrollTop = currentScroll;
        return;
      }

      if (Math.abs(delta) > 6) {
        if (delta > 0 && pastHeader) {
          this.hide();
        } else {
          this.reveal();
        }
      }

      this.lastScrollTop = currentScroll;
    }

    handleClick(event) {
      const selectedMenuLink = event.target.closest(SELECTORS.desktopMegaLink);
      if (selectedMenuLink && this.contains(selectedMenuLink)) {
        this.closeDesktopMenus(false);
        return;
      }

      const openMenu = this.getOpenDesktopMenu();
      if (openMenu && !openMenu.contains(event.target)) {
        this.closeDesktopMenus(false);
      }

    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      this.closeDesktopMenus(true);
    }

    handlePreventHeaderReveal() {
      this.preventReveal = true;
      window.clearTimeout(this.preventRevealTimer);
      this.preventRevealTimer = window.setTimeout(() => {
        this.preventReveal = false;
      }, 650);
    }

    handleMenuFocusOut(details, event) {
      const next = event.relatedTarget;
      if (next && details.contains(next)) return;
      this.scheduleCloseDesktopMenu(details, event);
    }

    getScrollTop() {
      return Math.max(window.pageYOffset || document.documentElement.scrollTop || 0, 0);
    }

    getHeaderThreshold() {
      return this.headerThreshold || 72;
    }

    hasOpenPanel() {
      const drawerDetails = this.querySelector(SELECTORS.drawerDetails);
      return Boolean(drawerDetails?.open || this.querySelector(SELECTORS.searchDetails) || this.getOpenDesktopMenu());
    }

    getOpenDesktopMenu() {
      return (this.desktopMenus || []).find((details) => details.open) || null;
    }

    syncMetrics() {
      const announcement = document.querySelector('.announcement-bar-section');
      const announcementHeight = announcement ? Math.ceil(announcement.getBoundingClientRect().height) : 0;
      const headerHeight = Math.ceil(this.getBoundingClientRect().height || this.offsetHeight || 0);
      this.headerThreshold = Math.max(headerHeight, 72);
      const rootStyle = document.documentElement.style;
      rootStyle.setProperty('--header-height', `${headerHeight}px`);
      rootStyle.setProperty('--rp-header-height', headerHeight + 'px');
      rootStyle.setProperty('--rp-header-sticky-offset', (headerHeight + announcementHeight + 16) + 'px');
      const productInfo = document.querySelector('product-info[id^="MainProduct-"] .product__info-container.product__column-sticky');
      if (productInfo) {
        const availableHeight = Math.max(0, window.innerHeight - headerHeight - announcementHeight - 32);
        productInfo.classList.toggle('rp-sticky-disabled', productInfo.getBoundingClientRect().height > availableHeight);
      }
      rootStyle.setProperty('--rp-announcement-height', `${announcementHeight}px`);
    }

    syncPanelTop() {
      const bottom = Math.ceil(this.getBoundingClientRect().bottom || 0);
      document.documentElement.style.setProperty('--rp-menu-panel-top', `${Math.max(0, bottom)}px`);
    }

    reveal(options = {}) {
      if (this.preventReveal && !options.force) return;
      this.section?.classList.remove('shopify-section-header-hidden');
      this.section?.classList.add('shopify-section-header-visible');
      document.documentElement.classList.remove('rp-header-hidden');
      document.documentElement.classList.add('rp-header-visible');
      if (this.hasOpenPanel()) this.syncPanelTop();
    }

    hide() {
      if (this.preventReveal || this.hasOpenPanel()) return;
      this.closeDesktopMenus(false);
      this.section?.classList.add('shopify-section-header-hidden');
      this.section?.classList.remove('shopify-section-header-visible');
      document.documentElement.classList.add('rp-header-hidden');
      document.documentElement.classList.remove('rp-header-visible');
    }

    syncDrawerState() {
      const drawerDetails = this.querySelector(SELECTORS.drawerDetails);
      const isOpen = Boolean(drawerDetails?.open);
      drawerDetails?.classList.toggle('menu-opening', isOpen);
      document.documentElement.classList.toggle('menu-drawer-open', isOpen);
      document.body.classList.toggle('overflow-hidden', isOpen);
      drawerDetails?.querySelector('summary')?.setAttribute('aria-expanded', String(isOpen));
      if (isOpen) this.reveal({ force: true });
    }

    openDesktopMenu(details) {
      window.clearTimeout(this.closeMenuTimer);
      this.desktopMenus.forEach((item) => {
        if (item !== details) {
          item.removeAttribute('open');
          item.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        }
      });
      if (!details.open) details.setAttribute('open', '');
      details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
      this.currentMenu = details;
      this.reveal({ force: true });
      this.syncPanelTop();
    }

    scheduleCloseDesktopMenu(details, event) {
      const next = event?.relatedTarget;
      if (next && details.contains(next)) return;
      window.clearTimeout(this.closeMenuTimer);
      this.closeMenuTimer = window.setTimeout(() => {
        const active = document.activeElement;
        if (active && details.contains(active)) return;
        details.removeAttribute('open');
        details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        if (this.currentMenu === details) this.currentMenu = null;
      }, 170);
    }

    closeDesktopMenus(restoreFocus = false) {
      const menuToFocus = this.currentMenu || this.getOpenDesktopMenu();
      this.desktopMenus.forEach((details) => {
        details.removeAttribute('open');
        details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      });
      this.currentMenu = null;
      if (restoreFocus) {
        window.requestAnimationFrame(() => menuToFocus?.querySelector('summary')?.focus({ preventScroll: true }));
      }
    }
  }

  if (!customElements.get('sticky-header')) {
    customElements.define('sticky-header', RPStickyHeader);
  }

  function refreshHeaderMetrics() {
    document.querySelectorAll('sticky-header').forEach((header) => {
      if (typeof header.syncMetrics === 'function') {
        header.syncMetrics();
        header.syncPanelTop();
        header.applyStickyState?.(true);
      }
    });
  }

  document.addEventListener('shopify:section:load', refreshHeaderMetrics);
  document.addEventListener('DOMContentLoaded', refreshHeaderMetrics, { once: true });

  window.RPHeaderController = {
    closeDrawer() {
      const drawer = document.querySelector('header-drawer');
      const summary = drawer?.querySelector('#Details-menu-drawer-container > summary');
      drawer?.closeMenuDrawer?.(new Event('rp:close-menu'), summary);
    },
    revealHeader() {
      document.querySelector('sticky-header')?.reveal?.({ force: true });
    },
    refresh: refreshHeaderMetrics,
  };
})();
